import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from '../common/Loader';
import Alert from '../common/Alert.js';
import routeService from '../../services/RouteService.js';
import { Route as RouteIcon, Pencil, ArrowLeft } from 'lucide-react';
import Button from '../common/Button.js';

const ViewRoute = () => {
  const { routeId } = useParams();
  const navigate = useNavigate();
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRoute = async () => {
      try {
        const response = await routeService.getById(routeId);
        setRoute(response.data || response);
      } catch (err) {
        Alert.error('Failed to load route details');
        console.error(err);
        navigate('/routes');
      } finally {
        setLoading(false);
      }
    };
    fetchRoute();
  }, [routeId, navigate]);

  const formatPath = () => {
    if (!route) return '—';
    const parts = [];
    if (route.from_location?.location) parts.push(route.from_location.location);
    (route.via_locations || []).forEach((h) => {
      if (h.location) parts.push(h.location);
    });
    if (route.to_location?.location) parts.push(route.to_location.location);
    return parts.join(' → ') || '—';
  };

  const loadingOrder = () => {
    if (!route) return '—';
    const parts = [];
    if (route.to_location?.location) parts.push(route.to_location.location);
    [...(route.via_locations || [])].reverse().forEach((h) => {
      if (h.location) parts.push(h.location);
    });
    return parts.join(' → ') || '—';
  };

  const hubLine = (hub) => {
    if (!hub?.location) return '—';
    return `${hub.location}${hub.location_code ? ` (${hub.location_code})` : ''}`;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading route details..." />
        </div>
      </DashboardLayout>
    );
  }

  if (!route) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto w-full min-w-0">
          <button
            onClick={() => navigate('/routes')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6 transition"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Routes
          </button>
          <div className="bg-white rounded-xl shadow p-6 text-center">
            <p className="text-gray-600">Route not found</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const viaCount = (route.via_locations || []).length;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto w-full min-w-0">
        {/* Back Button */}
        <button
          onClick={() => navigate('/routes')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6 transition font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Routes
        </button>

        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <RouteIcon className="w-8 h-8 text-buttonBg flex-shrink-0" />
              <div className="min-w-0">
                <h1 className="text-2xl sm:text-3xl font-bold text-secondary break-words">{route.name || 'Route'}</h1>
                <p className="text-gray-600 text-sm sm:text-base">Route ID: {route.route_id}</p>
              </div>
            </div>
            <span className="px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap bg-blue-100 text-blue-800 shrink-0">
              {viaCount} via stop{viaCount !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Main Details */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6">
          <h2 className="text-lg font-bold text-secondary mb-4 flex items-center gap-2">
            <RouteIcon className="w-5 h-5 text-secondary" />
            Route Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-4">
            {/* Travel Path */}
            <div className="md:col-span-2 bg-gray-50 rounded-xl p-4 border border-primary">
              <p className="text-xs sm:text-sm font-semibold text-secondary uppercase tracking-wide mb-2">Travel Path</p>
              <p className="font-semibold text-secondary text-base sm:text-lg break-words [overflow-wrap:anywhere]">
                {formatPath()}
              </p>
            </div>

            {/* ML Sheet Loading Order */}
            <div className="md:col-span-2 bg-orange-50 rounded-xl p-4 border border-orange-100">
              <p className="text-xs sm:text-sm font-semibold text-orange-800 uppercase tracking-wide mb-2">
                ML Sheet Loading Order
              </p>
              <p className="font-semibold text-orange-900 text-base sm:text-lg break-words [overflow-wrap:anywhere]">
                {loadingOrder()}
              </p>
              <p className="text-[11px] sm:text-xs text-orange-700 mt-1">
                Farthest destination first — load deep into the vehicle first.
              </p>
            </div>

            {/* From */}
            <div className="bg-gray-50 rounded-xl p-4 border border-primary">
              <p className="text-xs sm:text-sm font-semibold text-secondary uppercase tracking-wide mb-2">From</p>
              <p className="font-semibold text-secondary text-base sm:text-lg break-words">{hubLine(route.from_location)}</p>
            </div>

            {/* To */}
            <div className="bg-gray-50 rounded-xl p-4 border border-primary">
              <p className="text-xs sm:text-sm font-semibold text-secondary uppercase tracking-wide mb-2">To</p>
              <p className="font-semibold text-secondary text-base sm:text-lg break-words">{hubLine(route.to_location)}</p>
            </div>

            {/* Via Stops */}
            <div className="md:col-span-2 bg-gray-50 rounded-xl p-4 border border-primary">
              <p className="text-xs sm:text-sm font-semibold text-secondary uppercase tracking-wide mb-2">Via Stops</p>
              {viaCount === 0 ? (
                <p className="font-semibold text-secondary text-base sm:text-lg">None</p>
              ) : (
                <ul className="space-y-1.5 max-h-[9.5rem] overflow-y-auto pr-1">
                  {route.via_locations.map((h, idx) => (
                    <li
                      key={h.id}
                      className="font-medium text-secondary text-sm sm:text-base bg-white rounded-lg px-3 py-1.5 border border-gray-100 break-words"
                    >
                      {idx + 1}. {hubLine(h)}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button variant="secondary" icon={Pencil} onClick={() => navigate(`/routes/edit/${routeId}`)}>
            Edit Route
          </Button>
          <Button variant="secondary" onClick={() => navigate('/routes')}>
            Back to List
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ViewRoute;
